import React, { useState, useEffect } from 'react';
import { Container, Typography, TextField, Button, Alert, Select, MenuItem, InputLabel, FormControl, FormHelperText, Paper, styled, FormControlLabel, Checkbox } from '@mui/material';
import { db, storage } from '../firebase';
import { collection, addDoc, Timestamp, doc, getDoc, updateDoc } from 'firebase/firestore'; // Added doc, getDoc, updateDoc
import { logEditOperation } from '../utils/logger'; // Import logging function
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import imageCompression from 'browser-image-compression';
import { useAuth } from '../contexts/AuthContext';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import Banner from './Banner';
import { format, parseISO, getISOWeek } from 'date-fns'; // Import date-fns functions, added getISOWeek
import { getAuth } from "firebase/auth";
const auth = getAuth();
console.log("Current user at upload:", auth.currentUser);


const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiInputBase-root': {
    color: '#fff',
    backgroundColor: '#333',
    borderRadius: theme.shape.borderRadius,
    '&:hover': {
      backgroundColor: '#444',
    },
    '&.Mui-focused': {
      backgroundColor: '#444',
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: '#555',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: '#777',
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.primary.main,
    },
    // Style for the calendar icon in date inputs
    '& input[type="date"]::-webkit-calendar-picker-indicator': {
      filter: 'invert(1)',
    },
  },
  '& .MuiInputLabel-root': {
    color: '#aaa',
    '&.Mui-focused': {
      color: theme.palette.primary.main,
    },
  },
}));

const StyledFormControl = styled(FormControl)(({ theme }) => ({
  '& .MuiInputLabel-root': {
    color: '#aaa',
    '&.Mui-focused': {
      color: theme.palette.primary.main,
    },
  },
}));

const StyledSelect = styled(Select)(({ theme }) => ({
  color: '#fff',
  backgroundColor: '#333',
  borderRadius: theme.shape.borderRadius,
  '&:hover': {
    backgroundColor: '#444',
  },
  '&.Mui-focused': {
    backgroundColor: '#444',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: '#555',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: '#777',
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.primary.main,
  },
  '& .MuiSelect-icon': {
    color: '#fff',
  },
}));

// List of valid vendor names
const validVendorNames = [
  "Reliance Fresh",
  "Ashirwad Supermarket",
  "Local Purchase",
  "Water Can",
  "Housekeeping",
  "Hyperpure",
  "Ice Cream - Srinidhi Milk Products",
  "Coffee Bean - Ganesh Food Products",
  "Williams Bakery",
  "BBMP",
  "Repairs",
  "Misc"
];

function InvoiceEntry() {
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const { entryId } = useParams(); // Get entryId from URL parameters
  const [date, setDate] = useState(format(new Date(), 'yyyy-MM-dd')); // Default to present day using date-fns
  const [vendorName, setVendorName] = useState('');
  const [amount, setAmount] = useState('');
  const [imprestSaleCash, setImprestSaleCash] = useState(true); // true for 'Yes', false for 'No'
  const [vendorNameError, setVendorNameError] = useState(false); // State for vendor name validation error
  const [imageFile, setImageFile] = useState(null);
  const [existingImageUrl, setExistingImageUrl] = useState(null); // To store existing image URL
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Fetch entry data if entryId is present
  useEffect(() => {
    console.log('fetchEntry useEffect triggered. entryId:', entryId); // Log when useEffect is triggered
    const fetchEntry = async () => {
      console.log('Attempting to fetch document with ID:', entryId); // Log the document ID being fetched
      if (entryId) {
        setLoading(true);
        try {
          const docRef = doc(db, 'stockEntries', entryId);
          const docSnap = await getDoc(docRef);
          if (docSnap.exists()) {
            const data = docSnap.data();
            setDate(format(parseISO(data.date), 'yyyy-MM-dd'));
            setVendorName(data.vendorName);
            setAmount(data.amount.toString()); // Convert number to string for TextField
            setImprestSaleCash(data.imprestSaleCash !== 'No'); // Convert stored string to boolean
            setExistingImageUrl(data.imageUrl || null); // Set existing image URL
          } else {
            setError('Entry not found.');
          }
        } catch (err) {
          setError('Failed to fetch entry data.');
        } finally {
          setLoading(false);
        }
      }
    };
    fetchEntry();
  }, [entryId]);

  useEffect(() => {
    console.log('fetchEntry useEffect triggered. entryId:', entryId); // Log when useEffect is triggered
    const fetchEntry = async () => {
      console.log('Attempting to fetch document with ID:', entryId); // Log the document ID being fetched
      if (entryId) {
        setLoading(true);
        try {
          // ...fetch logic...
        } catch (error) {
          // ...error handling...
        } finally {
          setLoading(false);
        }
      }
    };
    fetchEntry();
  }, [entryId]);

  // Login check: if not logged in, redirect to login page
  if (!currentUser) {
    return <Navigate to="/login" replace />;
  }

  console.log('InvoiceEntry component rendered. entryId:', entryId); // Log entryId on render

  const handleVendorNameChange = (e) => {
    const value = e.target.value;
    setVendorName(value);
    // With a dropdown, validation is less critical here as only valid options are available,
    // but we keep the state update for consistency if needed elsewhere.
    setVendorNameError(false); // Assuming selection from dropdown is always valid
  };

  // Function to calculate week number using date-fns with ISO 8601 standard
  const getWeekNumber = (dateString) => {
    if (!dateString) return '';
    try {
      const dateObj = parseISO(dateString); // Parse the date string
      // Use getISOWeek for ISO 8601 week numbering
      return getISOWeek(dateObj); // Using getISOWeek for ISO 8601
    } catch (e) {
      console.error("Error parsing date or getting week number:", e);
      return '';
    }
  };

  const weekNumber = getWeekNumber(date);

  // Function to navigate back to the dashboard
  const handleBack = () => {
    navigate('/dashboard');
  };

 // Depend on entryId

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      console.log('File selected:', file);
      setImageFile(file);
      setExistingImageUrl(null); // Clear existing image URL when a new image is selected
      // Optional: Process image with OCR here
      
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!currentUser) {
      setError('You must be logged in to upload files. Please log in and try again.');
      return;
    }

    if (!date || !vendorName || !amount) { // Removed vendorNameError from this check as dropdown ensures valid selection
      setError('Please fill in all required fields.');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    let downloadURL = existingImageUrl; // Start with existing URL
    if (imageFile) {
      console.log('Processing image file for upload:', imageFile);
      console.log(`Original file size ${imageFile.size / 1024 / 1024} MB`);
      // Compress image before uploading
      const options = {
        maxSizeMB: 1,          // (default: Number.POSITIVE_INFINITY)
        maxWidthOrHeight: 1920, // (default: undefined)
        useWebWorker: true,
      };
      try {
        const compressedFile = await imageCompression(imageFile, options);
        console.log('Compressed file instance', compressedFile);
        console.log(`Compressed file size ${compressedFile.size / 1024 / 1024} MB`);

        const storageRef = ref(storage, `invoices/${currentUser.uid}/${Date.now()}_${compressedFile.name}`);
        const snapshot = await uploadBytes(storageRef, compressedFile);
        downloadURL = await getDownloadURL(snapshot.ref);
        console.log('Image uploaded successfully. Download URL:', downloadURL);
      } catch (compressionError) {
        // Log detailed error
        console.error('Error compressing image:', compressionError, imageFile);
        // Try uploading the original file as a fallback
        try {
          setError('Image compression failed, uploading original image...');
          const storageRef = ref(storage, `invoices/${currentUser.uid}/${Date.now()}_${imageFile.name}`);
          const snapshot = await uploadBytes(storageRef, imageFile);
          downloadURL = await getDownloadURL(snapshot.ref);
          setError(''); // Clear error if fallback succeeds
          console.log('Original image uploaded successfully. Download URL:', downloadURL);
        } catch (uploadError) {
          console.error('Error uploading original image after compression failure:', uploadError, imageFile);
          setError('Failed to upload image. Please try a different image or device.');
          setLoading(false);
          return; // Stop execution if upload fails
        }
      }
    }

    const entryData = {
      userId: currentUser.uid,
      date: date,
      vendorName: vendorName,
      amount: parseFloat(amount),
      imprestSaleCash: imprestSaleCash ? 'Yes' : 'No', // Convert boolean to string for storage // Ensure amount is stored as a number
      imageUrl: downloadURL, // Store the download URL
    };

    try {
      if (entryId) {
        // Update existing entry
        const docRef = doc(db, 'stockEntries', entryId);
        
        // Get the original document data for logging
        const docSnap = await getDoc(docRef);
        const oldData = docSnap.exists() ? docSnap.data() : null;
        
        // Update the document
        await updateDoc(docRef, entryData);
        
        // Log the edit operation
        if (oldData && currentUser) {
          await logEditOperation(currentUser.uid, entryId, oldData, entryData);
        }
        
        setSuccess('Stock entry updated successfully!');
      } else {
        // Add new entry
        await addDoc(collection(db, 'stockEntries'), {
          ...entryData,
          createdAt: Timestamp.now()
        });
        setSuccess('Stock entry saved successfully!');
        // Clear form fields after successful save (only for new entries)
        setDate('');
        setVendorName('');
        setAmount('');
        setImageFile(null);
        setExistingImageUrl(null);
      }
    } catch (dbError) {
      console.error('Database save error:', dbError);
      setError(`Failed to ${entryId ? 'update' : 'save'} stock entry.`);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Failed to log out:', error);
      // Optionally, display an error message to the user
    }
  };

  if (loading && entryId) {
    return <Container><Typography>Loading entry...</Typography></Container>;
  }

  return (
    <>
      <Banner
        title="Brewz Invoices"
        handleBack={handleBack}
        handleLogout={handleLogout}
      />
      <Container maxWidth="sm" sx={{ py: 4 }}>
        <Paper 
          component="form" 
          onSubmit={handleSave}
          sx={{
            p: { xs: 2, sm: 3 },
            backgroundColor: '#2a2a2a',
            color: '#fff',
            border: '2px solid #F3F8FF',
            borderRadius: 3,
            boxShadow: 4,
            display: 'flex',
            flexDirection: 'column',
            gap: 3
          }}
        >
          <Typography variant="h5" gutterBottom align="center">{entryId ? 'Edit Stock Entry' : 'Add New Invoice'}</Typography>

          {error && <Alert severity="error" sx={{ mb: 2, backgroundColor: '#5e2a2a', color: '#ffc6c6' }}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mb: 2, backgroundColor: '#2a5e3a', color: '#c6ffdd' }}>{success}</Alert>}
          <StyledTextField
            label="Date"
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            fullWidth
            InputLabelProps={{
              shrink: true,
            }}
          />
          {date && (
            <Typography variant="body2" sx={{ color: '#9BEC00', marginTop: '5px', textAlign: 'center' }}>
              THE DATE SELECTED IS IN WEEK : {weekNumber}
            </Typography>
          )}
          <StyledFormControl fullWidth required error={vendorNameError}>
            <InputLabel id="vendor-name-label">Vendor Name</InputLabel>
            <StyledSelect
              labelId="vendor-name-label"
              id="vendor-name-select"
              value={vendorName}
              label="Vendor Name"
              onChange={handleVendorNameChange}
              MenuProps={{
                PaperProps: {
                  sx: {
                    backgroundColor: '#333',
                    color: '#fff',
                  },
                },
              }}
            >
              {validVendorNames.map((name) => (
                <MenuItem key={name} value={name} sx={{ '&:hover': { backgroundColor: '#555' }, '&.Mui-selected': { backgroundColor: '#17ffc4', color: '#000', '&:hover': { backgroundColor: '#00e6a8' } } }}>
                  {name}
                </MenuItem>
              ))}
            </StyledSelect>
             {vendorNameError && <FormHelperText sx={{ color: '#f44336' }}>Invalid Vendor Name</FormHelperText>}
          </StyledFormControl>
          <StyledTextField
            label="Net Amount Paid"
            type="number"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={imprestSaleCash}
                onChange={(e) => setImprestSaleCash(e.target.checked)}
                sx={{
                  color: '#9BEC00',
                  '&.Mui-checked': {
                    color: '#9BEC00',
                  },
                }}
              />
            }
            label="Include in Settlement (Imprest/Sale Cash Only)"
            sx={{ color: '#fff' }}
          />
          <Button
            variant="outlined"
            component="label"
            sx={{ 
              color: '#9BEC00',
              borderColor: '#9BEC00',
              '&:hover': { borderColor: '#fff', backgroundColor: 'rgba(255, 255, 255, 0.1)' }
            }} 
          >
            Upload Image
            <input
              type="file"
              hidden
              accept="image/*"
              capture="environment"
              onChange={handleImageChange}
            />
          </Button>
          {/* <Typography variant="caption" sx={{ color: '#ffecb3', display: 'block', mt: 1 }}>
            On some Android devices (e.g., Vivo), the camera option may not appear. You can select an image from your gallery (which may have a camera shortcut), or try updating Chrome for better support.
          </Typography> */}
          {imageFile && <Typography variant="body2" sx={{ color: '#fff' }}>Selected file: {imageFile.name}</Typography>}
          {existingImageUrl && <Typography variant="body2" sx={{ color: '#fff' }}>Existing image: <a href={existingImageUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#bb86fc' }}>View Image</a></Typography>}
          

          <Button 
            type="submit" 
            variant="contained" 
            disabled={loading} 
            sx={{ 
              backgroundColor: '#06D001', 
              color: '#000', 
              fontWeight: 'bold',
              '&:hover': { backgroundColor: '#9BEC00' },
              '&.Mui-disabled': {
                backgroundColor: '#555',
                color: '#888'
              }
            }}
          >
            {loading ? 'Saving...' : entryId ? 'Update Entry' : 'Save Entry'}
          </Button>
        </Paper>
      </Container>
    </>
  );
}

export default InvoiceEntry;